<template>
    <div class="fill-height of-y d-flex flex-column">
        <v-scroll-x-transition hide-on-leave>
            <app-centre-loader v-if="loading" label="Fetching Data"/>
        </v-scroll-x-transition>

        <v-scroll-x-transition hide-on-leave>
            <div class="d-flex align-center justify-center flex-grow-1" v-if="!loading && !stats.summary">
                <div class="text-overline amber--text text-center">
                    No data for the selected date range
                    <br>
                    Please select another date or range
                </div>
            </div>
        </v-scroll-x-transition>

        <v-scroll-x-transition hide-on-leave>
            <div v-if="!loading && stats.summary" class="d-flex flex-column of-y flex-grow-1">
                <div class="flex-grow-1 of-y d-flex flex-column">
<!--Statistics-->
                    <app-divider label="Statistics" show-expanded :expanded.sync="statisticsShown"></app-divider>
                    <v-scroll-y-transition hide-on-leave>
                        <div class="d-flex align-start justify-space-around flex-wrap px-10 pt-4" v-if="statisticsShown">
                            <template v-for="card in shownCards">
                                <v-card class="mb-4 mx-2" width="300" :key="card.key" style="position: relative">

                                    <div class="pa-2 grey--text" style="position: absolute; right: 0; top: 0">
                                        {{ card.valDesc }}
                                    </div>

                                    <div class="pa-2">
                                        <div class="body-2">
                                            {{ card.info.text }}
                                        </div>
                                        <div class="caption grey--text">
                                            {{ card.info.description }}
                                        </div>
                                    </div>

                                    <div class="px-4 pb-2 text-h5 text-end">
                                        {{ card.val }}
                                    </div>
                                    <div>
                                        {{ card.val2 }}
                                    </div>
                                </v-card>
                            </template>
                        </div>
                    </v-scroll-y-transition>
<!--Screenshots-->
                    <template v-if="access.screenshots">
                        <app-divider label="Screenshots" show-expanded :expanded.sync="screenshotsShown"></app-divider>
                        <v-scroll-y-transition hide-on-leave>
                            <app-screenshot-preview v-if="screenshotsShown" :filter="{matches: [$store.state.ref.appStats.selectedApp]}"/>
                        </v-scroll-y-transition>
                    </template>
<!--Time Performance-->
                    <app-divider label="Time / Activity Breakdown" class="pt-4" show-expanded :expanded.sync="timePerformanceShown"></app-divider>
                    <v-scroll-y-transition hide-on-leave>
                        <div style="width: 100%; max-height: 500px" class="px-12 pt-4" v-if="timePerformanceShown">
                            <apex-chart height="300" type="line" :options="timePerformanceChartData.chartOptions"
                                        :series="timePerformanceChartData.series"></apex-chart>
                        </div>
                    </v-scroll-y-transition>
<!--Employee Performance-->
                    <app-divider label="Employee Performance" class="pt-4" show-expanded :expanded.sync="employeePerformanceShown"></app-divider>
                    <v-scroll-y-transition hide-on-leave>
                        <div style="width: 100%; max-height: 500px" class="px-12 pt-4" v-if="employeePerformanceShown">
                            <apex-chart height="300" type="bar" :options="employeePerformanceChartData.chartOptions"
                                        :series="employeePerformanceChartData.series"></apex-chart>
                        </div>
                    </v-scroll-y-transition>

<!--Top Teams-->
                    <app-divider label="Top Teams" show-expanded :expanded.sync="topTeamsShown" class="pt-4"></app-divider>
                    <v-scroll-y-transition hide-on-leave>
                        <div class="d-flex flex-wrap flex-grow-1" v-if="topTeamsShown">
                            <div class="flex-grow-1 flex-equal " style="min-width: 500px;">
                                <v-data-table
                                        :headers="topTeamHeadersAccess"
                                        :items="stats.topTeams"
                                        :items-per-page="stats.topTeams.length"
                                        hide-default-footer
                                        @click:row="gotoTeam($event.teamId)"
                                        :sortBy.sync="$store.state.sortPreference.appStats.topTeams.by"
                                        :sort-desc.sync="$store.state.sortPreference.appStats.topTeams.order"
                                >
                                    <template v-slot:item.teamName="{ item }">
                                        <div class="d-flex align-center overline">
                                            <v-icon left small>mdi-account-group</v-icon>
                                            {{ item.teamName }}
                                        </div>
                                    </template>

                                    <template v-slot:item.tt="{ item }">{{
                                            hourDisplay(item.tt, true)
                                        }}
                                    </template>
                                    <template v-slot:item.label="{ item }">
                                        <div class="d-flex align-center">
                                            <v-icon small :color="labelsRef[item.label].color" left>
                                                {{ labelsRef[item.label].icon }}
                                            </v-icon>
                                            <div class="body-2 d-flex align-center" :class="`${labelsRef[item.label].color}--text`">{{
                                                    labelsRef[item.label].text
                                                }}
                                            </div>
                                        </div>
                                    </template>

                                    <template v-slot:item.actions="{ item }">
                                        <v-btn
                                                color="primary"
                                                class="mx-2"
                                                small
                                                icon
                                                @click.stop="gotoTeamSettings(item.teamId)"
                                        >
                                            <v-icon>mdi-cog</v-icon>
                                        </v-btn>
                                    </template>

                                </v-data-table>
                            </div>
                            <div class="d-flex justify-center"
                                 style="min-width: 500px; width: 40%">
                                <div class="flex-grow-1 flex-equal">
                                    <div class="text-center subtitle-1">
                                        Top Teams Usage
                                    </div>
                                    <apex-chart type="donut" :options="topTeamsChartData.chartOptions"
                                                :series="topTeamsChartData.seriesData"></apex-chart>
                                </div>
                            </div>
                        </div>
                    </v-scroll-y-transition>
<!--Top Employees-->
                    <app-divider label="Top Employees" show-expanded :expanded.sync="topEmployeesShown" class="pt-4"></app-divider>
                    <v-scroll-y-transition hide-on-leave>
                        <div class="d-flex flex-wrap flex-grow-1" v-if="topEmployeesShown">
                            <div class="flex-grow-1 flex-equal " style="min-width: 500px;">
                                <v-data-table
                                        :headers="topEmployeeHeadersAccess"
                                        :items="stats.topEmployees"
                                        :items-per-page="stats.topEmployees.length"
                                        hide-default-footer
                                        class=""
                                        @click:row="gotoEmployee($event.employeeId)"
                                        :sortBy.sync="$store.state.sortPreference.appStats.topEmployees.by"
                                        :sort-desc.sync="$store.state.sortPreference.appStats.topEmployees.order"
                                >

                                    <template v-slot:item.employeeName="{ item }">
                                        <div class="d-flex align-center overline">
                                            <v-icon left small>mdi-account</v-icon>
                                            {{ item.employeeName }}
                                        </div>
                                    </template>
                                    <template v-slot:item.teamName="{ item }">
                                        <v-btn
                                                small
                                                text
                                                color="primary"
                                                @click.stop="gotoTeamSettings(item.teamId)"
                                        >
                                            <v-icon left>mdi-account-group</v-icon>
                                            {{ item.teamName }}
                                        </v-btn>
                                    </template>

                                    <template v-slot:item.tt="{ item }">{{
                                            hourDisplay(item.tt, true)
                                        }}
                                    </template>
                                    <template v-slot:item.label="{ item }">
                                        <div class="d-flex align-center">
                                            <v-icon small :color="labelsRef[item.label].color" left>
                                                {{ labelsRef[item.label].icon }}
                                            </v-icon>
                                            <div class="body-2 d-flex align-center" :class="`${labelsRef[item.label].color}--text`">{{
                                                    labelsRef[item.label].text
                                                }}
                                            </div>
                                        </div>
                                    </template>

                                    <template v-slot:item.actions="{ item }">
                                        <v-btn
                                                color="primary"
                                                class="mx-2"
                                                small
                                                icon
                                                @click.stop="gotoEmployeeSettings(item.employeeId)"
                                        >
                                            <v-icon>mdi-cog</v-icon>
                                        </v-btn>
                                    </template>
                                </v-data-table>
                            </div>
                            <div class="d-flex justify-center"
                                 style="min-width: 500px; width: 40%">
                                <div class="flex-grow-1 flex-equal">
                                    <div class="text-center subtitle-1">
                                        Top Employee Usage
                                    </div>
                                    <apex-chart type="donut" :options="topEmployeesChartData.chartOptions"
                                                :series="topEmployeesChartData.seriesData"></apex-chart>
                                </div>
                            </div>
                        </div>
                    </v-scroll-y-transition>
                </div>
            </div>
        </v-scroll-x-transition>

        <app-dlg
                v-if="$store.state.ref.appStats.showCardsSelection"
                :show-dlg.sync="$store.state.ref.appStats.showCardsSelection"
                title="Select Columns"
                :default-padding="false"
        >

            <app-list
                    v-model="$store.state.ref.appStats.selectedCards"
                    :items="cards"
                    :multiple="true"
                    headline-key="text"
                    item-key="value"
                    sub-line-key="description"
                    :show-check-boxes="true"
                    :return-object="false"
                    class="fill-height of-y"
                    :height="400"
                    @input="updateSelections"
            />

            <template v-slot:actions>
                <v-btn
                        @click="$store.state.ref.appStats.selectedCards = [...$store.state.defaultSelections.appStats]; updateSelections()"
                        small
                        class="ml-2"
                        color="blue"
                        outlined
                        size="small"
                >
                    Reset Selections
                </v-btn>
                <v-btn
                        @click="$store.state.ref.appStats.showCardsSelection = false"
                        small
                        class="ml-2"
                        color="secondary"
                        variant="contained"
                        size="small"
                >
                    Done
                </v-btn>
            </template>
        </app-dlg>
    </div>
</template>

<script>

import AppCentreLoader from "@/components/Shared/AppCentreLoader";
import {percentDisplay, hourDisplay, durationDisplay, durationDisplayFull} from "@/core/codeFunctions";
import AppDlg from "@/components/Shared/AppDlg";
import AppList from "@/components/Shared/AppList/AppList";
import {mapActions, mapGetters} from "vuex";
import AppDivider from "@/components/Shared/AppDivider/AppDivider";
import AppOutlinedChip from "@/components/Shared/AppOutlinedChip";
import VueApexCharts from 'vue-apexcharts'
import {format, isSameDay, isSameMonth, isSameYear} from "date-fns";
import AppScreenshotPreview from "@/components/Shared/Screenshots/AppScreenshotPreview";

export default {
    components: {AppScreenshotPreview, AppOutlinedChip, AppDivider, AppList, AppDlg, AppCentreLoader, apexChart: VueApexCharts},
    props: {},
    data() {
        return {
            screenshotsShown: true,
            statisticsShown: true,
            topEmployeesShown: true,
            topTeamsShown: true,
            timePerformanceShown: true,
            employeePerformanceShown: true,
            breakdownShown: true,
            hourDisplay,
            percentDisplay,
            durationDisplay,
            loading: false,
            stats: {
                summary: null,
            },
            cards: [
                {text: 'Label', value: 'label', description: 'Productivity Label'},
                {text: 'Active Time', value: 'tt', description: 'Total time active'},
                {text: 'Active Normal Time', value: 'ttn', description: 'Total time active in normal hours'},
                {text: 'Active Overtime', value: 'tto', description: 'Total time active in overtime hours'},
                {text: 'Usage Count', value: 'count', description: 'Total times app was used'},
            ],
            topEmployeeHeaders: [
                {text: 'Name', value: 'employeeName'},
                {text: 'Team', value: 'teamName'},
                {text: 'Total Duration', value: 'tt'},
                {text: 'Label', value: 'label'},
            ],
            topTeamHeaders: [
                {text: 'Name', value: 'teamName'},
                {text: 'Total Duration', value: 'tt'},
                {text: 'Label', value: 'label'},
            ],
        }
    },
    watch: {
        "$store.state.ref.dashboard.selectedRange.start": {
            immediate: true,
            handler(val) {
                this.refreshData()
            }
        },
        devicesWithAccessIds() {
            this.refreshData()
        }
    },
    mounted() {
        // this.refreshData();
    },
    methods: {
        ...mapActions(['gotoTeam', 'gotoDeviceSettings', 'gotoEmployee', 'gotoEmployeeSettings', 'gotoTeamSettings', 'updateSelections']),
        async refreshData() {
            try {
                this.loading = true;
                this.stats = await this.$newReq('POST', 'dashboard/getAppStats', {
                    start: this.start,
                    end: this.end,
                    app: this.$store.state.ref.appStats.selectedApp
                })
                // const breakDownMapped = breakdown.map(app => {
                //     return {
                //         name: app.name,
                //         data: app.data.map(val => {
                //             return {
                //                 label: val.x,
                //                 x: this.labelsRef[val.x].text,
                //                 y: val.y.map(dateStr => {
                //                     return new Date(dateStr).getTime()
                //                 }),
                //                 fillColor: this.labelsRef[val.x].cssColor
                //             }
                //         })
                //     }
                // })
                //
                // this.stats = {
                //     summary,
                //     topApps,
                //     breakdown: breakDownMapped,
                //     timePerformance
                // }
            } catch (e) {
                console.log(e)
                this.$snack.netWorkError();
            } finally {
                this.loading = false
            }
        }
    },
    computed: {
        ...mapGetters(['teamsRef', 'employeesRef', 'devicesByEmployee', 'labelsRef', 'access', 'devicesWithAccessIds']),

        topEmployeeHeadersAccess() {
            const cols = this.topEmployeeHeaders
            return this.access.setup
                    ? cols.concat({text: '', value: 'actions', sortable: false, align: 'center', width: 30})
                    : cols
        },

        topTeamHeadersAccess() {
            const cols = this.topTeamHeaders
            return this.access.setup
                    ? cols.concat({text: '', value: 'actions', sortable: false, align: 'center', width: 30})
                    : cols
        },

        employeePerformanceChartData() {
            const employeeNames = this.stats.employeePerformance.map(obj => obj.name)


            const series = [
                {
                    name: 'Usage',
                    data: this.stats.employeePerformance.map(obj => obj.tt),
                    // fillColor: this.labelsRef[0].cssColor
                }
            ]

            const chartOptions = {
                grid: {
                    show: true,
                    borderColor: 'var(--v-primary-darken4)',
                    strokeDashArray: 0,
                    position: 'back',
                    yaxis: {
                        lines: {
                            show: true
                        }
                    },
                },
                chart: {
                    type: 'bar',
                    height: 350,
                    toolbar: {
                        tools: {
                            download: false,
                        },
                    },
                },
                plotOptions: {
                    bar: {
                        horizontal: false,
                        columnWidth: '65%',
                        // endingShape: 'rounded',
                        borderRadius: 3,
                        dataLabels: {
                            position: 'top', // top, center, bottom
                        },
                    },
                },
                dataLabels: {
                    enabled: false,
                    formatter: function (val) {
                        return hourDisplay(val);
                    },
                    offsetY: -20,
                    style: {
                        fontSize: '12px',
                        colors: ["#fff"]
                    }
                },
                stroke: {
                    show: true,
                    width: 2,
                    colors: ['transparent']
                },
                xaxis: {
                    categories: employeeNames,
                    show: true,
                    rotate: -45,
                    rotateAlways: false,
                    labels: {
                        style: {
                            colors: employeeNames.map(() => '#fff'),
                        },
                    },

                    axisBorder: {
                        show: true,
                        color: 'var(--v-primary-base)',
                        height: 1,
                    },
                },
                yaxis: {
                    show: true,
                    title: {
                        text: 'Time Active (hours)'
                    },
                    labels: {
                        show: true,
                        align: 'right',
                        style: {
                            colors: ['#FFF'],
                            fontSize: '14px',
                            fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                        },
                        formatter: (value) => { return `${(value / 3600).toFixed(2)}`},
                    },
                },
                fill: {
                    type: 'solid',
                    colors: [this.labelsRef[0].cssColor, this.labelsRef[1].cssColor, this.labelsRef[2].cssColor]
                },

                tooltip: {
                    enabled: true,
                    theme: 'dark',
                    style: {
                        fontSize: '12px',
                        fontFamily: undefined
                    },
                    y: {
                        formatter: function (val) {
                            return durationDisplay(val)
                        },
                        title: {
                            formatter: (seriesName) => seriesName,
                        },
                    },
                    marker: {
                        show: true,
                    },
                },

                legend: {
                    markers: {
                        fillColors: [this.labelsRef[0].cssColor, this.labelsRef[1].cssColor, this.labelsRef[2].cssColor]
                    },
                    labels: {
                        colors: ['#fff']
                    },
                }
            }

            return {
                series,
                chartOptions,
            }
        },

        timePerformanceChartData() {

            // Get Min And Max Values
            const min = this.stats.timePerformance.reduce((val, item) => val === null || item.bs < val ? item.bs : val, null)
            const max = this.stats.timePerformance.reduce((val, item) => val === null || item.bs > val ? item.bs : val , null)
            // Get A List Of Teams
            const teams = this.stats.timePerformance.reduce((list, item) => list.includes(item.teamName) ? list : list.concat(item.teamName), [])
            const timeNames = [];
            const seriesData = {}
            teams.forEach(teamName => {
                seriesData[teamName] = {
                    name: teamName,
                    data: []
                }
            })
            for (let k = min; k <= max; k = k + 900 ) {
                timeNames.push(`${(Math.floor(k / 3600) + 2).toString().padStart(2, '0')}:${Math.floor((k % 3600) / 60).toString().padStart(2, '0')}`)
                for (const team of teams) {
                    const record = this.stats.timePerformance.find(obj => obj.teamName === team && obj.bs === k)
                    seriesData[team].data.push(record && record.tt ? record.tt : 0)
                }
            }

            const series = Object.values(seriesData)

            const chartOptions = {
                chart: {
                    height: 350,
                    type: 'line',
                    zoom: {enabled: false},
                    toolbar: {
                        tools: {
                            download: false,
                        },
                    },
                    animations: {
                        enabled: true,
                        easing: 'easeinout',
                        speed: 800,
                        animateGradually: {
                            enabled: false,
                        },
                    },
                },
                dataLabels: {
                    enabled: false
                },
                stroke: {
                    curve: 'smooth',
                    width: 2
                },
                grid: {
                    show: true,
                    borderColor: 'var(--v-primary-base)',
                    strokeDashArray: 0,
                    position: 'back',
                    yaxis: {
                        lines: {
                            show: false
                        }
                    },
                },
                xaxis: {
                    categories: timeNames,
                    labels: {
                        rotate: -60,
                        rotateAlways: true,
                        style: {
                            colors: timeNames.map(() => '#fff'),
                        },
                    },
                    axisBorder: {
                        show: true,
                        color: 'var(--v-primary-base)',
                        height: 1,
                    },
                },
                tooltip: {
                    enabled: false,
                    theme: 'dark',
                    y: {
                        formatter: function (val) {
                            return durationDisplay(val)
                        },
                        title: {
                            formatter: (seriesName) => seriesName,
                        },
                    },
                },
                yaxis: {
                    show: true,
                    title: {
                        text: 'Activity'
                    },
                    labels: {
                        show: false,
                    },
                },
                legend: {
                    labels: {
                        colors: ['#fff']
                    },
                },
            }

            return {
                series,
                chartOptions,
            }
        },

        topEmployeesChartData() {

            const series = [...this.stats.topEmployees].sort((a, b) => a.label > b.label ? 1 : -1)

            const seriesNames = series.map(obj => obj.employeeName)

            const seriesData = series.map(obj => obj.tt)

            const seriesColors = series.map(obj => this.labelsRef[obj.label].cssColor)

            const chartOptions = {
                labels: seriesNames,
                grid: {
                    show: false,
                },
                dataLabels: {
                    enabled: false,
                    background: {
                        enabled: false,
                    },
                    style: {colors: ['#fff']},
                    dropShadow: {enabled: false,},
                    formatter: function (val, opts) {
                        return hourDisplay(seriesData[opts.seriesIndex])
                    },
                },
                stroke: {
                    opacity: 0,
                    colors: seriesColors,
                    width: 0
                },
                fill: {
                    opacity: 1,
                    colors: seriesColors
                },
                chart: {
                    type: 'donut',
                },
                legend: {
                    show: true,
                    position: 'right',
                    labels: {
                        colors: ['#FFF']
                    },
                    markers: {
                        fillColors: series.map(app => {
                            return this.labelsRef[app.label].cssColor
                        }),
                    },
                },
                yaxis: {show: false},
                plotOptions: {
                    donut: {
                        rings: {
                            strokeWidth: 0
                        },
                        spokes: {
                            strokeWidth: 0
                        },
                    }
                },
                tooltip: {
                    fillSeriesColor: true,
                    custom: function ({series, seriesIndex, dataPointIndex, w}) {
                        return ` <div class="pa-1"> <div class="caption">${seriesNames[seriesIndex]}</div> <div class="body-1 text-right">${durationDisplay(seriesData[seriesIndex])}</div> </div> `
                    }
                },
                // theme: {
                //     mode: 'dark',}
            }
            return {
                seriesData,
                chartOptions,
            }
        },

        topTeamsChartData() {

            const series = [...this.stats.topTeams].sort((a, b) => a.label > b.label ? 1 : -1)

            const seriesNames = series.map(obj => obj.teamName)

            const seriesData = series.map(obj => obj.tt)

            const seriesColors = series.map(obj => this.labelsRef[obj.label].cssColor)

            const chartOptions = {
                labels: seriesNames,
                grid: {
                    show: false,
                },
                dataLabels: {
                    enabled: false,
                    background: {
                        enabled: false,
                    },
                    style: {colors: ['#fff']},
                    dropShadow: {enabled: false,},
                    formatter: function (val, opts) {
                        return hourDisplay(seriesData[opts.seriesIndex])
                    },
                },
                stroke: {
                    opacity: 0,
                    colors: seriesColors,
                    width: 0
                },
                fill: {
                    opacity: 1,
                    colors: seriesColors
                },
                chart: {
                    type: 'donut',
                },
                legend: {
                    show: true,
                    position: 'right',
                    labels: {
                        colors: ['#FFF']
                    },
                    markers: {
                        fillColors: series.map(app => {
                            return this.labelsRef[app.label].cssColor
                        }),
                    },
                },
                yaxis: {show: false},
                plotOptions: {
                    donut: {
                        rings: {
                            strokeWidth: 0
                        },
                        spokes: {
                            strokeWidth: 0
                        },
                    }
                },
                tooltip: {
                    fillSeriesColor: true,
                    custom: function ({series, seriesIndex, dataPointIndex, w}) {
                        return ` <div class="pa-1"> <div class="caption">${seriesNames[seriesIndex]}</div> <div class="body-1 text-right">${durationDisplay(seriesData[seriesIndex])}</div> </div> `
                    }
                },
                // theme: {
                //     mode: 'dark',}
            }
            return {
                seriesData,
                chartOptions,
            }
        },

        shownCards() {
            return this.$store.state.ref.appStats.selectedCards.map(key => {
                const card = {
                    key,
                    info: this.cards.filter(card => card.value === key).pop(),
                    val: (() => {
                        switch (key) {
                            case 'count':
                                return `${this.stats.summary[key]} ${this.stats.summary[key] === 1 ? 'Count' : 'Counts'}`
                            default:
                                return durationDisplayFull(this.stats.summary[key])
                        }
                    })(),
                    // valDesc: (() => {
                    //     if (['count'].includes(key))
                    //         return null
                    //     return hourDisplay(this.stats.summary[key])
                    // })(),
                }


                return card
            })
        },

        start() {
            return this.$store.state.ref.dashboard.selectedRange.start
        },
        end() {
            return this.$store.state.ref.dashboard.selectedRange.end
        },
        dateTitle() {
            // TODO Add Here
            // if (this.range.title)
            //     return this.range.title
            if (!isSameYear(this.range.start, this.range.end))
                return `${format(this.range.start,'dd MMM yyyy')} - ${format(this.range.end,'dd MMM yyyy')}`
            if (!isSameMonth(this.range.start, this.range.end))
                return `${format(this.range.start,'dd MMM')} - ${format(this.range.end,'dd MMM yyyy')}`
            if (!isSameDay(this.range.start, this.range.end))
                return `${format(this.range.start,'dd')} - ${format(this.range.end,'dd MMM yyyy')}`
            return format(this.range.end,'dd MMM yyyy')
        },
        range() {
            return this.$store.state.ref.dashboard.selectedRange
        },
    },
}
</script>

<style scoped>

</style>